import { memo } from 'react';

import { TriangleAlertIcon } from '@air/design-icons';

import { workspaceAgentPanelPill } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelPillChrome';

/** `reference` is a moniker with no pill behind it at all, so not even its subject is known. */
export type WorkspaceAgentUnavailablePillKind =
  | 'asset'
  | 'automation'
  | 'board'
  | 'brandKitImage'
  | 'reference'
  | 'shareLink'
  | 'showcase'
  | 'skill';

const UNAVAILABLE_PILL_LABELS: Record<WorkspaceAgentUnavailablePillKind, string> = {
  asset: 'Unavailable asset',
  automation: 'Unavailable automation',
  board: 'Unavailable board',
  brandKitImage: 'Unavailable brand kit image',
  reference: 'Unavailable reference',
  shareLink: 'Unavailable share link',
  showcase: 'Unavailable showcase',
  skill: 'Unavailable skill',
};

/** Only a set of assets is ever counted; every other kind stands for exactly one subject. */
const UNAVAILABLE_PILL_PLURAL_LABELS: Partial<Record<WorkspaceAgentUnavailablePillKind, string>> = {
  asset: 'Unavailable assets',
};

/** A set that lost exactly one member reads as the single-subject label, not "(1)". */
const getUnavailablePillLabel = (kind: WorkspaceAgentUnavailablePillKind, count: number | undefined) => {
  const pluralLabel = UNAVAILABLE_PILL_PLURAL_LABELS[kind];

  return count === undefined || count === 1 || !pluralLabel
    ? UNAVAILABLE_PILL_LABELS[kind]
    : `${pluralLabel} (${count})`;
};

export type WorkspaceAgentUnavailablePillProps = {
  /** Set when the chip stands in for a whole set, so the label can say how many went missing. */
  count?: number;
  kind: WorkspaceAgentUnavailablePillKind;
  /** Shown only on hover, for reporting a bad reference. */
  referenceId: string;
};

export const WorkspaceAgentUnavailablePill = memo(
  ({ count, kind, referenceId }: WorkspaceAgentUnavailablePillProps) => (
    <span
      className={workspaceAgentPanelPill({ tone: 'error' })}
      data-testid="WORKSPACE_AGENT_MESSAGE_UNAVAILABLE_PILL"
      title={referenceId}
    >
      <TriangleAlertIcon aria-hidden className="shrink-0" size="xs" />
      <span className="truncate">{getUnavailablePillLabel(kind, count)}</span>
    </span>
  ),
);

WorkspaceAgentUnavailablePill.displayName = 'WorkspaceAgentUnavailablePill';
